import { Button, Card, Checkbox, Divider, message, Radio, RadioChangeEvent, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import inviteIcon from 'assets/images/invite-icon.svg';
import { useUpdateDependentRights } from 'all-in-one-shared-api';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { useLocation, useNavigate } from 'react-router-dom';
import { DependentData } from '../DependentsPage';
import { MessageType } from '../../../models/MessageType';
import { mutate } from 'swr';
import { ProfileInfoKey, useProfileInfoContext } from 'contexts/profileInfo';

const ManageDependentsPage = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const {
    name,
    memberType,
    shortfallRight: defaultShortfallRight,
    submitAndViewValue: defaultSubmitAndViewValue,
  } = state as DependentData;
  const [submitAndViewValue, setSubmitAndViewValue] = useState(defaultSubmitAndViewValue);
  const [shortfallRight, setShortfallRight] = useState(defaultShortfallRight);
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();

  const { isValidating: isValidatingProfile } = useProfileInfoContext();

  const { shouldFetch, isLoading, data } = useUpdateDependentRights(
    'updateDependentRights',
    keycloak.token ?? '',
    memberType,
    submitAndViewValue,
    shortfallRight,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      onError: (err) => {
        shouldFetch(false);
        if (err.code === 'setting.dependent.missing.email') {
          message[MessageType.ERROR](t('settings.dependent.missing.email'));
        } else {
          message[MessageType.ERROR](t('error.generic'));
        }
      },
    },
  );

  useEffect(() => {
    if (data && data.success) {
      // Thuy's note: need a little delay so that ProfileInfo will be revalidated with new data of dependent
      setTimeout(async () => {
        await mutate(ProfileInfoKey);
        navigate('/dependents');
        message[MessageType.SUCCESS](t('profile.updateSuccess'));
      }, 200);
    }
  }, [data, shouldFetch, navigate, t]);

  const onRadioChange = (e: RadioChangeEvent) => {
    setSubmitAndViewValue(e.target.value);
  };

  const onCheckboxChange = (e: CheckboxChangeEvent) => {
    setShortfallRight(e.target.checked);
  };

  return (
    <>
      <div style={{ alignItems: 'alignLeft', display: 'flex', marginLeft: 4, flexDirection: 'column' }}>
        <Space direction="horizontal">
          <img src={inviteIcon} alt="registration-icon" />
          <Typography.Title level={2}>{t('profile.manageDependent')}</Typography.Title>
        </Space>
        <Card style={{ width: '100%', borderRadius: 8, padding: 16, marginTop: 36 }}>
          <Typography.Title level={4}>{name}</Typography.Title>
          <Divider style={{ width: '40%' }} />
          <Space direction="vertical">
            <Typography.Text>{t('profile.submitAndViewLabel')}</Typography.Text>
            <Radio.Group onChange={onRadioChange} value={submitAndViewValue} style={{ marginBottom: 16 }}>
              <Space direction="vertical">
                <Radio value={true}>{t('profile.forAllFamily')}</Radio>
                <Radio value={false}>{t('profile.forSelfOnly')}</Radio>
              </Space>
            </Radio.Group>
            <Typography.Text>{t('profile.viewAndPay')}</Typography.Text>
            <Checkbox onChange={onCheckboxChange} checked={shortfallRight}>
              {t('profile.viewAndPayCheck')}
            </Checkbox>
            <Button
              block
              type="primary"
              loading={isLoading || isValidatingProfile}
              size="large"
              style={{ marginTop: 36, width: '40%' }}
              onClick={() => {
                shouldFetch(true);
              }}
            >
              {t('editClaim.update')}
            </Button>
          </Space>
        </Card>
      </div>
    </>
  );
};

export default ManageDependentsPage;
